import type { RootState } from '@app/store';

import { asyncThunkCreator, buildCreateSlice, PayloadAction } from '@reduxjs/toolkit';

import { addProductFromComparison } from '@screens/Comparison/api/addProductToComparison/addProductFromComparison';
import { removeProductFromComparison } from '@screens/Comparison/api/removeProductFromComparison/removeProductFromComparison';
import { HttpCodes, HttpStatuses } from '@shared/types/enums/http.enum';
import { Metrics } from '@shared/types/enums/metrics.enum';
import { getCookiesProductsForUser, setCookiesProductsForUser } from '@shared/utils/cookiesUtils/cookiesUtils';
import { sendMetrics } from '@shared/utils/sendMetrics/sendMetrics';
import { openAlert } from '@widgets/AlertModal';

import { ComparisonState } from '../types/comparisonState';

const createAppSlice = buildCreateSlice({
  creators: { asyncThunk: asyncThunkCreator }
});

export const initialState: ComparisonState = {
  productIds: []
};

export const comparisonSlice = createAppSlice({
  name: 'comparison',
  initialState,
  reducers: (create) => ({
    updateComparisonList: create.reducer((state, action: PayloadAction<string[]>) => {
      state.productIds = action.payload;
    }),
    addToComparisonThunk: create.asyncThunk(
      async (id: string, thunkAPI) => {
        const res = await addProductFromComparison([id]);

        switch (res.code) {
          case HttpCodes.UNAUTHORIZED: {
            const cookiesComparesCount = getCookiesProductsForUser<string[]>('comparesCount');

            setCookiesProductsForUser({
              comparesCount: [...cookiesComparesCount, id]
            });

            break;
          }
          case HttpCodes.CREATED: {
            break;
          }
          default: {
            thunkAPI.dispatch(
              openAlert({
                data: {
                  title: 'Ошибка',
                  text: 'Попробуйте позже'
                },
                variant: HttpStatuses.ERROR
              })
            );
            return thunkAPI.rejectWithValue(HttpStatuses.ERROR);
          }
        }

        return id;
      },
      {
        fulfilled: (state, action) => {
          state.productIds.push(action.payload);

          sendMetrics(Metrics.COMPARE_GOAL);
        }
      }
    ),
    removeFromComparisonThunk: create.asyncThunk(
      async (id: string, thunkAPI) => {
        const res = await removeProductFromComparison([id]);

        switch (res.code) {
          case HttpCodes.UNAUTHORIZED: {
            const cookiesComparesCount = getCookiesProductsForUser<string[]>('comparesCount');

            const comparesCount = cookiesComparesCount.filter((item) => item !== id);

            setCookiesProductsForUser({
              comparesCount
            });

            break;
          }
          case HttpCodes.OK: {
            break;
          }
          default: {
            thunkAPI.dispatch(
              openAlert({
                data: {
                  title: 'Ошибка',
                  text: 'Попробуйте позже'
                },
                variant: HttpStatuses.ERROR
              })
            );

            return thunkAPI.rejectWithValue(HttpStatuses.ERROR);
          }
        }

        return id;
      },
      {
        fulfilled: (state, action) => {
          state.productIds = state.productIds.filter((el) => el !== action.payload);
        }
      }
    ),
    clearAllThunk: create.asyncThunk(
      async (_: undefined, thunkAPI) => {
        const productIds = thunkAPI.getState() as RootState;

        const res = await removeProductFromComparison(productIds.comparison.productIds);

        switch (res.code) {
          case HttpCodes.UNAUTHORIZED: {
            setCookiesProductsForUser({
              comparesCount: undefined
            });

            break;
          }
          case HttpCodes.OK: {
            break;
          }
          default: {
            thunkAPI.dispatch(
              openAlert({
                data: {
                  title: 'Ошибка',
                  text: 'Попробуйте позже'
                },
                variant: HttpStatuses.ERROR
              })
            );
            return thunkAPI.rejectWithValue(HttpStatuses.ERROR);
          }
        }
      },
      {
        fulfilled: (state) => {
          state.productIds = [];
        }
      }
    ),
    clearCategoryThunk: create.asyncThunk(
      async (
        {
          removeIds,
          category,
          actualProductIds
        }: { removeIds: string[]; category: string; actualProductIds: string[] },
        thunkAPI
      ) => {
        const res = await removeProductFromComparison(removeIds);

        switch (res.code) {
          case HttpCodes.UNAUTHORIZED: {
            const cookiesComparesCount = getCookiesProductsForUser<string[]>('comparesCount');

            const comparesCount = cookiesComparesCount.filter((item) => !removeIds.includes(item));

            setCookiesProductsForUser({
              comparesCount
            });

            break;
          }
          case HttpCodes.OK: {
            break;
          }
          default: {
            thunkAPI.dispatch(
              openAlert({
                data: {
                  title: 'Ошибка',
                  text: 'Попробуйте позже'
                },
                variant: HttpStatuses.ERROR
              })
            );
            return thunkAPI.rejectWithValue(HttpStatuses.ERROR);
          }
        }

        const data: { category: string; actualProductIds: string[] } = {
          category,
          actualProductIds
        };

        return data;
      },
      {
        fulfilled: (state, action) => {
          state.productIds = action.payload.actualProductIds;
        }
      }
    )
  })
});

export const {
  updateComparisonList,
  clearAllThunk,
  removeFromComparisonThunk,
  clearCategoryThunk,
  addToComparisonThunk
} = comparisonSlice.actions;

export const comparisonReducer = comparisonSlice.reducer;
